import { FirestoreSharedKeys } from "shared/firestore";
import {
  ApplicationCreatedBy,
  ApplicationPlan,
  ApplicationStatus,
  IApplication,
  IUninitaliedApplication,
  ApplicationPlanSubType
} from "shared/model/IApplication";
import { ICoworkBranch } from "shared/model/ICoworkSpace";

import MixPanelAnalyticsService from "../../../services/Analytics/MixPanelAnalyticsService";
import FirebaseAuthService from "../../../services/firebase/auth/FirebaseAuthService";
import FirebaseDatabaseService from "../../../services/firebase/database/FirebaseDatabseService";
import FirestoreListObserver from "../../../services/firebase/database/observers/FirestoreListObserver";
import FirestoreObserverFactory from "../../../services/firebase/database/observers/FirestoreObserverFactory";
import FirebaseFunctionsService from "../../../services/firebase/functions/FirebaseFunctionsService";
import FirebaseStorageService from "../../../services/firebase/storage/FirebaseStorageService";
import RavenService from "../../../services/Logging/RavenService";
import Utils from "../../../services/utils/Utils";
import { Dispatch } from "../../store/store";
import AuthSelectors from "../auth/AuthSelectors";
import { IState } from "./..";
import { IApplicationCreationProps } from "./../../model/IApplicationCreationProps";
import ApplicationsSelectors from "./ApplicationsSelectors";

const APPLICATIONS_LIMIT = 60;

export default class ApplicationsEffects {
  public static reloadApplications = async (params: {}, state: IState) => {
    try {
      Dispatch.applications.reloadingApplications({});
      const coworks = await FirebaseDatabaseService.getMyCoworkingSpaces();

      if (coworks.length > 0) {
        const cowork = coworks[0];
        const currentUserEmail = AuthSelectors.currentUserEmail(state.auth);
        const branchName = ApplicationsSelectors.branchForCurrentUserInCowork(
          currentUserEmail!,
          cowork
        );
        const applications = await FirebaseDatabaseService.getApplications(
          cowork.name,
          branchName,
          APPLICATIONS_LIMIT
        );
        const allBranches = await FirebaseDatabaseService.getAllCoworkingSpaceBranches(
          cowork.name
        );

        if (!allBranches || allBranches.length === 0) {
          throw new Error(
            `No branches for ${cowork.name} ${branchName} ${currentUserEmail}`
          );
        }

        const selectedBranchName =
          branchName || FirestoreSharedKeys.DefaultBranchName;
        const branch = allBranches.filter(
          aBranch => aBranch.name === selectedBranchName
        )[0];

        Dispatch.applications.reloadApplicationsSucceeded({
          coworkingSpace: cowork!,
          applications,
          currentUserCoworkBranch: branch,
          branches: allBranches
        });

        ApplicationsEffects.observeApplicationUpdates(cowork.name);
      } else {
        RavenService.captureMessage(
          `No coworking spaces for current user ${JSON.stringify(state.auth)}`
        );
        await FirebaseAuthService.signOut();
        window.location.reload();
      }
    } catch (error) {
      MixPanelAnalyticsService.trackError({ error: `${error}` });
      console.error(error);
      Dispatch.applications.reloadApplicationsFailed(`${error}`);
    }
  };

  public static observeApplicationUpdates = async (coworkName: string) => {
    if (ApplicationsEffects.applicationsObserver) {
      ApplicationsEffects.applicationsObserver.unsubscribe();
    }

    ApplicationsEffects.applicationsObserver = FirestoreObserverFactory.applicationsListObserver(
      coworkName
    );
    ApplicationsEffects.applicationsObserver.subscribe(applications => {
      Dispatch.applications.applicationsUpdate({ applications });
    });
  };

  public static setCurrentBranch = async (
    params: {
      branch: ICoworkBranch;
    },
    state: IState
  ) => {
    try {
      Dispatch.applications.reloadingApplications({
        currentUserCoworkBranch: params.branch
      });

      const { branch } = params;

      const allBranches = ApplicationsSelectors.allBranches(state.applications);
      const currentCowork = ApplicationsSelectors.currentCowork(
        state.applications
      );
      const isDefaultBranch =
        branch.name === FirestoreSharedKeys.DefaultBranchName;
      const applications = await FirebaseDatabaseService.getApplications(
        currentCowork!.name,
        isDefaultBranch ? null : branch.name,
        APPLICATIONS_LIMIT
      );

      Dispatch.applications.reloadApplicationsSucceeded({
        coworkingSpace: currentCowork!,
        applications,
        currentUserCoworkBranch: branch,
        branches: allBranches
      });
    } catch (error) {
      MixPanelAnalyticsService.trackError({ error: `${error}` });
      console.error(error);
      Dispatch.applications.reloadApplicationsFailed(`${error}`);
    }
  };

  public static createApplication = async (
    params: IApplicationCreationProps,
    state: IState
  ) => {
    const { pdfFile, membershipType, branch, amount, email, name } = params;

    try {
      const currentUser = FirebaseAuthService.currentUserBasicData();
      if (!currentUser) {
        throw new Error("Must be logged in");
      }

      const cowork = ApplicationsSelectors.currentCowork(state.applications);

      if (!cowork || !branch || !amount || !email || !name) {
        throw new Error(
          `No current cowork or branch name for user, can't create application ${cowork} ${JSON.stringify(
            params
          )}`
        );
      }

      Dispatch.applications.creatingApplication();

      let remoteFile;
      if (pdfFile) {
        remoteFile = await FirebaseStorageService.uploadContractFile(pdfFile);
      }

      const application: IUninitaliedApplication = {
        status: ApplicationStatus.Pending,
        contract: remoteFile,
        manager: currentUser,
        createdAt: FirebaseDatabaseService.timestamp(),
        coworkName: cowork.name,
        coworkDisplayName: cowork.displayName,
        branchName: branch.name,
        branchDisplayName: branch.displayName,
        branchState: branch.state,
        applicant: {
          name: name.trim()
        },
        applicantEmail: email.trim(),
        amount,
        membershipType,
        plan: ApplicationPlan.Deposit,
        planSubType: ApplicationPlanSubType.Waive,
        metadata: { createdBy: ApplicationCreatedBy.Manager }
      };

      Utils.removeUndefinedKeys(application);

      const updatedApplication = await FirebaseDatabaseService.addApplication(
        cowork.name,
        application
      );

      const monthlyFeeResponse = await FirebaseFunctionsService.getDepositPaymentInfo(
        {
          id: updatedApplication.uid!,
          cowork: cowork.name
        }
      );

      if (
        monthlyFeeResponse.ok &&
        monthlyFeeResponse.data &&
        monthlyFeeResponse.data!.monthlyFee
      ) {
        console.log(`Monthly fee is ${monthlyFeeResponse.data.monthlyFee}`);
      } else {
        throw new Error(
          `Failed getting monthly fee ${JSON.stringify(monthlyFeeResponse)}`
        );
      }

      const applicationWithMonthlyFee: IApplication = {
        ...updatedApplication,
        monthlyPaymentAmount: monthlyFeeResponse.data!.monthlyFee
      };

      Dispatch.applications.createApplicationSucceeded({
        application: applicationWithMonthlyFee
      });

      MixPanelAnalyticsService.trackApplicationCreated(updatedApplication);
    } catch (error) {
      Dispatch.applications.createApplicationFailed(
        `Whoops, failed creating application. ${error}`
      );
      RavenService.captureException(error, {
        message: "Failed creating application"
      });
    }
  };

  private static applicationsObserver?: FirestoreListObserver<IApplication>;
}



// WEBPACK FOOTER //
// ./src/state/modules/applications/ApplicationsEffetcs.ts